import CompanyOnboardingModal from "components/company_onboarding/CompanyOnboardingModal";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { SessionContext } from "contexts/SessionContext";
import { CompanyContext } from "contexts/CompanyContext";
import { HiPencil, HiOutlineUserGroup, HiUser } from "react-icons/hi2";
import { TbBuildingStore, TbLanguage, TbNetwork, TbPalette, TbSpeakerphone, TbTargetArrow, TbWorldWww } from "react-icons/tb";
import StepConnectSocialNetworks from "components/company_onboarding/StepConnectSocialNetworks";
import StepProfile from "components/company_onboarding/StepProfile";
import StepCompanyWebsite from "components/company_onboarding/StepCompanyWebsite";
import StepCompanyProfile from "components/company_onboarding/StepCompanyProfile";
import { api as Api } from "api/http";
import StepCompanyBrandVoice from "components/company_onboarding/StepCompanyBrandVoice";
import { COMPANY_ONBOARDING_REQUIRED } from "components/ui/utils/constant";
import StepCompanyBrandStyle from "components/company_onboarding/StepCompanyBrandStyle";
import ConnectASocialNetworkModal from "components/layout/ConnectASocialNetworkModal";
import { Notification, toast } from "components/ui";

const CompanyOnboardingContext = React.createContext({
    isOpened: false,
    currentStep: 0,
    steps: [],
    nonOnboardingSteps: [],
    completionPercentage: 0,
    isCompleted: false,
    open: (stepId = null) => void 0,
    close: () => void 0,
    nextStep: (...props) => void 0,
    prevStep: () => void 0,
    goToStep: (stepId) => void 0,
    setCurrentStepData: (data) => void 0,
    currentStepData: null
});

const CompanyOnboardingProvider = ({ children }) => {
    const [ isOpened, setIsOpened ] = useState(false); 
    const [ currentStep, setCurrentStep ] = useState(0);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ completionPercentage, setCompletionPercentage ] = useState(0);
    const [ fullCompletionPercentage, setFullCompletionPercentage ] = useState(0);
    const [ currentStepData, setCurrentStepData ] = useState(null);
    const [ stepCustomHandlers, setStepCustomHandlers ] = useState({});
    const { user, updateUserData } = useContext(SessionContext);
    const { company, companySocials, updateCompany, isLoadingCompanySocials } = useContext(CompanyContext);

    const steps = useMemo(() => [
        {
            id: 'profile',
            title: 'Complete Your Profile',
            description: 'Add your profile image and name',
            icon: <HiUser />,
            isComplete: !!(user?.name),
            component: <StepProfile />,
            isAsync: true,
            validate: (props) => {
                props = { ...user, ...props };
                if (!props?.name?.trim()) {
                    return 'Please enter your name';
                }
                return true;
            },
            action: async (props) => {
                Api.put(`/users/${user.id}`, {}, props).then(() => {
                    updateUserData();
                });
            }
        },
        {
            id: 'company_website',
            title: 'Complete Company Website',
            description: 'Add your company website',
            icon: <TbWorldWww />,
            isComplete: !!(company?.website),
            isOptional: true,
            component: <StepCompanyWebsite />,
            action: async (props) => {
                await updateCompany(props);
            }
        },
        {
            id: 'company_profile',
            title: 'Complete Company Profile',
            description: 'Add your company description',
            icon: <TbBuildingStore />,
            isComplete: !!(company?.name && company?.description),
            component: <StepCompanyProfile />,
            isAsync: true,
            validate: (props) => {
                props = { ...company, ...props };
                if (!props?.name?.trim()) {
                    return 'Please enter your company name';
                }
                if (!props?.description?.trim()) {
                    return 'Please enter your company description';
                }
                return true;
            },
            action: async (props) => {
                await updateCompany(props);
            }
        },
        {
            id: 'brand_voice',
            title: 'Set Company Brand Voice',
            description: 'Set your tone, topics and audiences',
            icon: <TbSpeakerphone />,
            isComplete: !!(company?.language && (company?.audiences?.length || company?.topics?.length || company?.tones?.length || company?.language_types?.length)),
            component: <StepCompanyBrandVoice />,
            isAsync: true,
            validate: (props) => {
                props = { ...company, ...props };
                if (!props?.language?.trim()) {
                    return 'Please select a language';
                }
                if (!props?.audiences?.length && !props?.topics?.length && !props?.tones?.length && !props?.language_types?.length) {
                    return 'Please select at least one audience, topic, tone and language type';
                }
                return true;
            },
            action: async (props) => {
                await updateCompany(props);
            }
        },
        {
            id: 'brand_style',
            title: 'Set Company Brand Style',
            description: 'Set your tone, topics and audiences',
            icon: <TbPalette />,
            isComplete: !!(company?.image?.url && !!company?.palettes?.length),
            isOptional: true,
            component: <StepCompanyBrandStyle />,
            action: async (props) => {
                if (props?.image?.url && props?.image?.url == company?.image?.url) {
                    // Logo unchanged — drop it so updateCompany skips the re-upload.
                    const { image, ...rest } = props;
                    await updateCompany(rest);
                    return;
                }
                await updateCompany(props);
            }
        },
        // {
        //     id: 'prompts',
        //     title: 'Setup Prompts',
        //     description: 'Configure content generation prompts',
        //     icon: <TbTargetArrow />,
        //     isComplete: company?.prompts?.length > 0,
        //     // action: () => navigate('/company-settings/prompts')
        // },
    ], [user, company, companySocials]);

    const nonOnboardingSteps = useMemo(() => {
        return [
            {
                id: 'social_networks',
                title: 'Connect Social Networks',
                description: 'Connect your social media accounts',
                icon: <TbNetwork />,
                isComplete: companySocials?.length > 0,
                component: <ConnectASocialNetworkModal />
            },
            // {
            //     id: 'team',
            //     title: 'Onboard Your Team',
            //     description: 'Invite team members to collaborate',
            //     icon: <HiOutlineUserGroup />,
            //     isComplete: company?.team_members?.length > 1,
            //     component: <StepCompanyTeam />,
            // }
        ]
    }, [user, company, companySocials]);

    const isCompleted = useMemo(() => {
        return steps.every(step => step.isOptional || step.isComplete);
    }, [steps]);

    const isFullCompleted = useMemo(() => {
        return fullCompletionPercentage === 100;
    }, [fullCompletionPercentage]);

    const open = (stepId = null) => {
        if (stepId) {
            const stepIndex = steps.findIndex(step => step.id === stepId);
            setCurrentStep(stepIndex !== -1 ? stepIndex : 0);
        } else {
            const firstIncompleteStep = steps.findIndex(step => !step.isComplete);
            setCurrentStep(firstIncompleteStep !== -1 ? firstIncompleteStep : 0);
        }
        localStorage.removeItem(`recentlyCreatedCompany`);
        setIsOpened(true);
    };

    const close = () => {
        setIsOpened(false);
    };

    const nextStep = async (props) => {
        const currentStepObj = steps[currentStep];
        
        // Skip validation for optional steps
        if (!currentStepObj.isOptional) {
            const validationResult = currentStepObj.validate?.(props);
            if (validationResult !== true) {
                toast.push(
                    <Notification 
                        title="Validation Error"
                        type="warning"
                    >
                        {validationResult}
                    </Notification>
                );
                return;
            }
        }

        if (currentStepObj?.action) {
            if (currentStepObj?.isAsync) {
                currentStepObj.action(props).then(() => {});
            } else {
                await currentStepObj.action(props);
            }
        }
        
        // Clear current step data
        setCurrentStepData(null);
        
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            close();
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            // Clear current step data when going back
            setCurrentStepData(null);
            setCurrentStep(prev => prev - 1);
        }
    };

    const goToStep = async (stepId) => {
        // Auto-save current step data before navigating
        if (currentStepData && Object.keys(currentStepData).length > 0) {
            const currentStepObj = steps[currentStep];
            if (currentStepObj?.action) {
                try {
                    // Skip validation for optional steps when auto-saving
                    if (!currentStepObj.isOptional && currentStepObj.validate) {
                        const validationResult = currentStepObj.validate(currentStepData);
                        // Only save if validation passes
                        if (validationResult === true) {
                            if (currentStepObj?.isAsync) {
                                currentStepObj.action(currentStepData).then(() => {});
                            } else {
                                await currentStepObj.action(currentStepData);
                            }
                        }
                    } else {
                        // For optional steps or steps without validation, just save
                        if (currentStepObj?.isAsync) {
                            currentStepObj.action(currentStepData).then(() => {});
                        } else {
                            await currentStepObj.action(currentStepData);
                        }
                    }
                } catch (error) {
                    console.error('Error auto-saving step data:', error);
                }
            }
        }

        // Clear current step data and navigate to new step
        setCurrentStepData(null);
        const stepIndex = steps.findIndex(step => step.id === stepId);
        if (stepIndex !== -1) {
            setCurrentStep(stepIndex);
        }
    };

    useEffect(() => {
        if (!!company?.id && !isLoadingCompanySocials) {
            const completedSteps = steps.filter(step => step.isComplete).length;
            setCompletionPercentage(Math.round((completedSteps / steps.length) * 100));
            setIsLoading(false);
        }
    }, [steps, company, isLoadingCompanySocials]);

    useEffect(() => {
        if (!!company?.id && !isLoadingCompanySocials) {
            const completedSteps = [...steps, ...nonOnboardingSteps].filter(step => step.isComplete).length;
            setFullCompletionPercentage(Math.round((completedSteps / [...steps, ...nonOnboardingSteps].length) * 100));
            setIsLoading(false);
        }
    }, [steps, nonOnboardingSteps, company, isLoadingCompanySocials]);

    useEffect(() => {
        if (isLoading) {
            close();
            return;
        }
        if (
            !window.location.pathname.includes("appsumo") && 
            !window.location.pathname.includes("subscription") &&  
            !window.location.pathname.includes("settings")
        ) {
            if (
                (COMPANY_ONBOARDING_REQUIRED && !isOpened && !isCompleted && user?.companies?.length == 1) || 
                (user?.companies?.length && !isOpened && !!localStorage.getItem(`recentlyCreatedCompany`) && !isCompleted)
            ) {
                open();
                return;
            }
        }
    }, [company, steps, isCompleted, isLoading, user?.companies?.length]);

    return (
        <CompanyOnboardingContext.Provider value={{ 
            isOpened,
            currentStep,
            steps,
            nonOnboardingSteps,
            completionPercentage,
            fullCompletionPercentage,
            isCompleted,
            isFullCompleted,
            open,
            close,
            nextStep,
            prevStep,
            goToStep,
            setCurrentStepData,
            currentStepData,
            setStepCustomHandlers,
            stepCustomHandlers
        }}>
            {children}
        </CompanyOnboardingContext.Provider>
    );
};

export { CompanyOnboardingContext, CompanyOnboardingProvider };